import { useQuery } from '@apollo/client';
import { Stack, Text, Alert, Hidden, Box } from 'braid-design-system';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import Routes from 'src/config/routes';
import { GetListingProductsForPackagesPageDocument } from 'src/generated/graphql';
import { fireSelectedPrivateSellerSelectedProductLoggedInEvent } from 'src/utils/analytics';
import { getSelectedProduct } from 'src/utils/cookies';
import { validateListingProducts } from 'src/utils/listingProducts';

import { DesktopPackageColumns } from './components/DesktopPackageColumns';
import { HeaderMessage } from './components/HeaderMessage';
import { MobilePackageTiles } from './components/MobilePackageTiles';

export const PackagesContainer = () => {
  const selectedProduct = getSelectedProduct();
  const [searchParams] = useSearchParams();
  const listingId = searchParams.get('listingId');

  if (selectedProduct && listingId) {
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('listingId', listingId);
    newSearchParams.set('productToken', selectedProduct.toLowerCase());

    return (
      <Navigate
        to={`${Routes.PAYMENT_CONFIRM}?${newSearchParams.toString()}`}
      />
    );
  }

  return <Packages listingId={listingId} />;
};

interface PackagesProps {
  listingId: string | null;
}

const Packages = (props: PackagesProps) => {
  const navigate = useNavigate();

  const { loading, data, error } = useQuery(
    GetListingProductsForPackagesPageDocument,
  );

  if (loading) {
    return <CustomLoader />;
  }

  if (error || !data || data.listingProducts.length === 0) {
    return (
      <Alert
        tone="critical"
        data={{ testid: 'get-listing-products-error-notice' }}
      >
        <Text>
          There was an error getting packages. Please try again later.
        </Text>
      </Alert>
    );
  }

  const validatedProducts = validateListingProducts(data.listingProducts);

  if (validatedProducts.kind === 'error') {
    return (
      <Alert tone="critical" data={{ testid: 'product-validation-error' }}>
        <Text>
          There was an error getting packages. Please try again later.
        </Text>
      </Alert>
    );
  }

  const { accelerate, freemium, standard } = validatedProducts.singleProducts;

  const handleFreeClick = () => {
    fireSelectedPrivateSellerSelectedProductLoggedInEvent(freemium);

    const newSearchParams = new URLSearchParams();
    if (props.listingId) {
      newSearchParams.set('listingId', props.listingId);
    }

    navigate(`${Routes.FREEMIUM_CONTRACT}?${newSearchParams.toString()}`);
  };

  const handleStandardClick = () => {
    fireSelectedPrivateSellerSelectedProductLoggedInEvent(standard);

    const newSearchParams = new URLSearchParams();
    newSearchParams.set('productToken', standard.productToken);
    if (props.listingId) {
      newSearchParams.set('listingId', props.listingId);
    }

    navigate(`${Routes.PAYMENT}?${newSearchParams.toString()}`);
  };

  const handleAccelerateClick = () => {
    fireSelectedPrivateSellerSelectedProductLoggedInEvent(accelerate);

    const newSearchParams = new URLSearchParams();
    newSearchParams.set('productToken', accelerate.productToken);
    if (props.listingId) {
      newSearchParams.set('listingId', props.listingId);
    }

    navigate(`${Routes.PAYMENT}?${newSearchParams.toString()}`);
  };

  return (
    <Stack space="large">
      <HeaderMessage />

      <Hidden below="desktop">
        <Box data={{ testid: 'desktop-package-columns' }}>
          <DesktopPackageColumns
            onFreemiumClick={handleFreeClick}
            onStandardClick={handleStandardClick}
            onAccelerateClick={handleAccelerateClick}
            htags={htags}
          />
        </Box>
      </Hidden>

      <Hidden above="tablet">
        <Box data={{ testid: 'mobile-package-tiles' }}>
          <MobilePackageTiles
            onFreemiumClick={handleFreeClick}
            onStandardClick={handleStandardClick}
            onAccelerateClick={handleAccelerateClick}
            htags={htags}
          />
        </Box>
      </Hidden>
    </Stack>
  );
};

const htags = {
  freemium: 'AUSBF6',
  standard: 'AUSBSTD',
  accelerate: 'AUSBA',
};
